import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

// Styled components
const PoemContainer = styled.div`
  text-align: center;
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding: 40px 20px;
  box-sizing: border-box;
`;

const TitleWrapper = styled.div`
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 2em;
  color: #333;
  font-family: 'Cormorant Garamond', serif;
  font-weight: 300;
`;

const Subtitle = styled.p`
  color: #666;
  font-family: 'Cormorant Garamond', serif;
  margin-top: 10px;
`;

const PoemContent = styled.div`
  position: relative;
  z-index: 1;
  font-size: 1.2em;
  line-height: 1.6;
  width: 90%;
  max-width: 800px;
  color: #333;
  margin-bottom: 30px;
  font-family: 'Cormorant Garamond', serif;
  text-align: left;
  
  @media (max-width: 900px) {
    width: 95%;
  }
`;

const PoemLine = styled.p`
  margin-bottom: 20px;
  white-space: nowrap;
  overflow: visible;
  
  @media (max-width: 768px) {
    white-space: normal;
  }
`;

const SwappableWord = styled.span`
  font-weight: 500;
  color: #555;
  border-bottom: 1px dotted #999;
  cursor: pointer;
  transition: color 0.3s;
  
  &:hover {
    color: #000;
  }
`;

const DarkModeButton = styled.button`
  position: fixed;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  color: #333;
  cursor: pointer;
  padding: 10px;
  z-index: 10;
`;

// Initial data for the poem with binary options
const initialPoemData = [
  {
    id: 1,
    parts: [
      { type: 'text', content: 'There\'s a ' },
      { type: 'swappable', optionA: 'jasmine plant', optionB: 'rabid deer', currentWord: 'jasmine plant' },
      { type: 'text', content: ' in my backyard.' },
    ],
  },
  {
    id: 2,
    parts: [
      { type: 'text', content: 'It\'s ' },
      { type: 'swappable', optionA: 'home', optionB: 'lost', currentWord: 'home' },
      { type: 'text', content: ' here too.' },
    ],
  },
  {
    id: 3,
    parts: [
      { type: 'text', content: 'My ' },
      { type: 'swappable', optionA: 'uncle\'s', optionB: 'brother\'s', currentWord: 'uncle\'s' },
      { type: 'text', content: ' birthday is this week.' },
    ],
  },
  {
    id: 4,
    parts: [
      { type: 'text', content: 'I sent ' },
      { type: 'swappable', optionA: 'a denim jacket', optionB: '40 pineapples', currentWord: 'a denim jacket' },
      { type: 'text', content: ' and a ' },
      { type: 'swappable', optionA: 'smile.', optionB: 'hug.', currentWord: 'smile.' },
    ],
  },
  {
    id: 5,
    parts: [
      { type: 'swappable', optionA: 'Working', optionB: 'Exercise', currentWord: 'Exercise' },
      { type: 'text', content: ' is building ' },
      { type: 'swappable', optionA: 'strength', optionB: 'pain', currentWord: 'strength' },
      { type: 'text', content: ' in my spine.' },
    ],
  },
  {
    id: 6,
    parts: [
      { type: 'text', content: 'It ' },
      { type: 'swappable', optionA: 'helps', optionB: 'hurts', currentWord: 'helps' },
      { type: 'text', content: ' when I go to a friend\'s dinner party.' },
    ],
  },
  {
    id: 7,
    parts: [
      { type: 'text', content: 'The preschool next door is given ' },
      { type: 'swappable', optionA: 'nap time.', optionB: 'whistles.', currentWord: 'nap time' },
    ],
  },
  {
    id: 8,
    parts: [
      { type: 'text', content: 'I wish I could be ' },
      { type: 'swappable', optionA: 'there too.', optionB: 'far away.', currentWord: 'there too.' },
    ],
  },
  {
    id: 9,
    parts: [
      { type: 'text', content: 'How is life so ' },
      { type: 'swappable', optionA: 'chaotic?', optionB: 'surprising?', currentWord: 'chaotic?' },
    ],
  },
];

// Component for a single line of the poem
function PoemLineComponent({ line, onWordClick }) {
  return (
    <PoemLine>
      {line.parts.map((part, index) =>
        part.type === 'swappable' ? (
          <SwappableWord
            key={index}
            onClick={() => onWordClick(line.id, index)}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onWordClick(line.id, index);
              }
            }}
          >
            {part.currentWord}
          </SwappableWord>
        ) : (
          <span key={index}>{part.content}</span>
        )
      )}
    </PoemLine>
  );
}

// Main Poem Component
function Poem() {
  const [poemLines, setPoemLines] = useState(() => 
    initialPoemData.map(line => ({
      ...line,
      parts: line.parts.map(part => 
        part.type === 'swappable' ? { ...part, currentWord: part.optionA } : part
      )
    }))
  );

  const [lastChanged, setLastChanged] = useState(null);

  // Handles the direct toggling of a swappable word
  const handleWordToggle = useCallback((lineId, partIndex) => {
    setPoemLines((prevLines) =>
      prevLines.map((line) => {
        if (line.id === lineId) {
          const updatedParts = line.parts.map((part, idx) => {
            if (idx === partIndex && part.type === 'swappable') {
              const nextWord = part.currentWord === part.optionA 
                               ? part.optionB 
                               : part.optionA;
              return { ...part, currentWord: nextWord };
            }
            return part;
          });
          return { ...line, parts: updatedParts };
        }
        return line;
      })
    );
    setLastChanged({ lineId, partIndex });
  }, []);
  
  // Resets the poem to its initial state
  const resetPoem = () => {
    setPoemLines(initialPoemData.map(line => ({
      ...line,
      parts: line.parts.map(part => 
        part.type === 'swappable' ? { ...part, currentWord: part.optionA } : part
      )
    })));
    setLastChanged(null);
  };

  return (
    <div>
      <TitleWrapper>
        <Title>Two Weeks</Title>
        <Subtitle>Click on the highlighted words to explore alternative versions.</Subtitle>
      </TitleWrapper>

      <PoemContent>
        {poemLines.map((line) => (
          <PoemLineComponent key={line.id} line={line} onWordClick={handleWordToggle} />
        ))}
      </PoemContent>
    </div>
  );
}

// Main App Component
export default function App() {
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setDarkMode(true);
    }
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => setDarkMode(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    document.body.style.backgroundColor = darkMode ? '#333' : '#fff';
    document.body.style.color = darkMode ? '#eee' : '#333';
  }, [darkMode]);

  return (
    <PoemContainer>
      <DarkModeButton onClick={() => setDarkMode(!darkMode)}>
        {darkMode ? '☀️' : '🌙'}
      </DarkModeButton>
      <Poem />
    </PoemContainer>
  );
}
